import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import ProfileMenu from './ProfileMenu';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export const EnhancedAppHeader = ({ title, subtitle, action }) => {
  const navigate = useNavigate();
  const { activeOrg, switchOrganization, getUserOrganizations } = useAuth();
  const userOrgs = getUserOrganizations();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const hasMultipleOrgs = userOrgs.length > 1;

  const handleOrgSwitch = (orgId) => {
    switchOrganization(orgId);
    window.location.reload();
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-lg bg-card/80 border-b border-border shadow-soft">
      <div className="container mx-auto px-4 py-3 sm:py-4">
        {/* Top Row: Logo/Org + Profile */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          {/* Left: Logo + Org Name - Clickable to go home */}
          <div 
            className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-primary shrink-0" />
            
            {hasMultipleOrgs ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button 
                    variant="ghost" 
                    className="h-auto p-1 sm:p-2 hover:bg-primary/10 min-w-0 max-w-[200px] sm:max-w-none"
                  >
                    <span className="font-semibold text-sm sm:text-base truncate">
                      {isMobile && activeOrg?.name && activeOrg.name.length > 20
                        ? `${activeOrg.name.slice(0, 20)}...`
                        : activeOrg?.name || 'Loading...'
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 ml-1 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel>Switch Organization</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {userOrgs.map((org) => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => handleOrgSwitch(org.id)}
                      className={activeOrg && org.id === activeOrg.id ? 'bg-muted' : ''}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="truncate">{org.name}</span>
                        {activeOrg && org.id === activeOrg.id && (
                          <Badge variant="outline" className="text-xs ml-2">Active</Badge>
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="font-semibold text-sm sm:text-base truncate">
                {isMobile && activeOrg?.name && activeOrg.name.length > 20
                  ? `${activeOrg.name.slice(0, 20)}...`
                  : activeOrg?.name || 'Loading...'
                }
              </span>
            )}
          </div>

          {/* Right: Notifications + Profile Menu */}
          <div className="flex items-center gap-2">
            {/* Notification Bell */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {/* Notification Badge - shows when there are notifications */}
                  <span className="absolute top-1 right-1 h-2 w-2 bg-destructive rounded-full" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No new notifications
                </div>
                {/* Future: Add actual notifications here */}
              </DropdownMenuContent>
            </DropdownMenu>
            
            <ProfileMenu isMobile={isMobile} />
          </div>
        </div>

        {/* Bottom Row: Page Title and Action */}
        {(title || action) && (
          <div className="flex items-center justify-between gap-4">
            {title && (
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold gradient-text truncate">
                  {title}
                </h1>
                {subtitle && (
                  <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            )}
            {action && <div className="shrink-0">{action}</div>}
          </div>
        )}
      </div>
    </header>
  );
};

export default EnhancedAppHeader;